'use client';

import { Button, Divider, Image, Typography } from '@musinsa/mds';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { pathsMusinsa } from '../../common';
import { useReverseProposalDetail } from '../hooks/useReverseProposalDetail';
import { BrandHeader } from './common/BrandHeader';

interface ReverseProposalDetailPageViewProps {
  reverseProposalId: number;
}

const STATUS_LABELS: Record<string, string> = {
  WAITING: '협업 수락 대기중',
  APPROVED: '협업 수락',
  REJECTED: '협업 거절',
};

export const ReverseProposalDetailPageView = ({
  reverseProposalId,
}: ReverseProposalDetailPageViewProps) => {
  const router = useRouter();
  const { data: detail, isLoading, isError } = useReverseProposalDetail(reverseProposalId);

  if (isLoading) {
    return null;
  }

  if (isError || !detail) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-3">
        <Typography variant="body13pxReg" colorScheme="gray-600">
          제안 정보를 불러올 수 없습니다.
        </Typography>
        <button type="button" onClick={() => router.back()}>
          <Typography variant="body13pxReg" colorScheme="blue">
            돌아가기
          </Typography>
        </button>
      </div>
    );
  }

  const statusText = STATUS_LABELS[detail.status] ?? '';
  const isApproved = detail.status === 'APPROVED';
  const statusColorScheme = isApproved ? 'blue' : 'gray-600';

  return (
    <div className="flex flex-col flex-1 bg-white">
      {/* 브랜드 정보 + 상태 */}
      <div className="px-4 pt-6">
        <BrandHeader
          imageUrl={detail.brandImageUrl}
          name={detail.brandName}
          statusText={statusText}
          statusColorScheme={statusColorScheme}
        />
      </div>

      {/* 제안 정보 — 레거시 ReverseProposalInfo: Padding top/bottom 1, left/right 4 */}
      <div className="flex justify-between px-4 pt-3 pb-1">
        <Typography colorScheme="black" variant="body13pxReg">
          제안일
        </Typography>
        <Typography colorScheme="black" variant="body13pxReg">
          {detail.createdAt}
        </Typography>
      </div>
      <div className="flex justify-between px-4 py-1">
        <Typography colorScheme="black" variant="body13pxReg">
          제안번호
        </Typography>
        <Typography colorScheme="black" variant="body13pxReg">
          {String(detail.campaignReverseProposalId)}
        </Typography>
      </div>
      <div className="flex justify-between px-4 pt-1 pb-3">
        <Typography colorScheme="black" variant="body13pxReg">
          캠페인 시작 희망일
        </Typography>
        <Typography colorScheme="black" variant="body13pxReg">
          {detail.startAt}
        </Typography>
      </div>

      {/* Divider — 레거시: paddingOptions top 3, bottom 3, left 4, right 4 */}
      <div className="px-4 py-3">
        <Divider variant="line" />
      </div>

      {/* 상품 정보 */}
      <div className="px-4">
        <div className="pt-3 pb-1">
          <Typography colorScheme="black" variant="body14pxMed">
            쿠폰 적용 희망 상품
          </Typography>
        </div>
        {/* ProductItem — 레거시: Padding top 3 bottom 3 + flex items-center + Image 72x86 + ml-3 */}
        <a
          href={`${process.env.NEXT_PUBLIC_MUSINSA_BASE_URL}/products/${detail.goods.goodsNo}`}
          className="flex items-center py-3"
        >
          <div className="w-[72px] h-[86px] rounded overflow-hidden relative shrink-0">
            <Image src={detail.goods.imageUrl} alt={detail.goods.goodsName} size="size72x86" />
          </div>
          <div className="flex flex-col w-full ml-3 overflow-hidden [&>span]:truncate">
            <Typography variant="body13pxSemi">{detail.goods.brandName}</Typography>
            <Typography variant="body13pxReg" className="mt-[2px]!">
              {detail.goods.goodsName}
            </Typography>
            <span className="mt-[2px]! block">
              {detail.goods.salesRate > 0 && (
                <Typography variant="body13pxSemi" colorScheme="red" className="mr-[2px]">
                  {detail.goods.salesRate}%
                </Typography>
              )}
              <Typography variant="body13pxSemi">
                {detail.goods.price.toLocaleString()}원
              </Typography>
            </span>
          </div>
        </a>

        {/* 수락 시 브랜드 캠페인 제안 보기 버튼 */}
        {isApproved && detail.curatorCouponCampaignProposalId && (
          <Link
            href={pathsMusinsa.campaignProposalDetail({
              query: { id: detail.curatorCouponCampaignProposalId },
            })}
          >
            <Button variant="secondary" className="w-full h-9">
              브랜드의 캠페인 제안 보기
            </Button>
          </Link>
        )}
      </div>
    </div>
  );
};
